
import React, { useState } from 'react'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Icon } from '@/components/ui/icon'
import type { Canvas } from '@/hooks/useCanvas'

interface CanvasDeletePermanentModalProps {
  isOpen: boolean
  onClose: () => void
  canvas: Canvas | null
  onDeletePermanent: (canvasId: string) => Promise<void>
}

export const CanvasDeletePermanentModal: React.FC<CanvasDeletePermanentModalProps> = ({
  isOpen,
  onClose,
  canvas,
  onDeletePermanent,
}) => {
  const [isLoading, setIsLoading] = useState(false)

  const handleDeletePermanent = async () => {
    if (!canvas) return

    setIsLoading(true)
    try {
      await onDeletePermanent(canvas.uuid)
      onClose()
    } catch (error) {
      console.error('Failed to permanently delete canvas:', error)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <AlertDialog open={isOpen} onOpenChange={onClose}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2 text-destructive">
            <Icon name="alert-triangle" className="h-5 w-5" />
            Delete Forever
          </AlertDialogTitle>
          <AlertDialogDescription>
            Are you sure you want to permanently delete &quot;{canvas?.title}&quot;?
            <br />
            <span className="font-medium text-destructive">
              This action cannot be undone and the session will be lost forever.
            </span>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isLoading}>
            Cancel
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={handleDeletePermanent}
            disabled={isLoading}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90 focus:ring-destructive"
          >
            {isLoading ? (
              <>
                <Icon name="loader-2" className="mr-2 h-4 w-4 animate-spin" />
                Deleting Forever...
              </>
            ) : (
              <>
                <Icon name="trash-2" className="mr-2 h-4 w-4" />
                Delete Forever
              </>
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}